import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, X, Upload, Image as ImageIcon, Briefcase, FileImage, CheckCircle, Plus, Trash2, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const MAX_SERVICE_IMAGES = 5;
const MAX_BRAND_IMAGES = 3;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const getUserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const getAccessToken = () => {
  return (
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    ""
  );
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    ...additionalHeaders,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

const formatBackendErrors = (data) => {
  if (!data) return "Something went wrong.";

  if (typeof data === "string") return data;

  if (Array.isArray(data)) {
    return data.map((item) => formatBackendErrors(item)).join("\n");
  }

  if (typeof data === "object") {
    const messages = [];

    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((msg) => messages.push(`${key}: ${msg}`));
      } else if (typeof value === "object" && value !== null) {
        const nested = formatBackendErrors(value);
        if (nested) messages.push(nested);
      } else if (value) {
        messages.push(`${key}: ${value}`);
      }
    });

    if (messages.length) return messages.join("\n");
  }

  return "Something went wrong.";
};

const createPreviewItem = ({
  file = null,
  existingId = null,
  existingUrl = "",
  name = "",
  size = "",
}) => ({
  id: existingId ?? `new_${Date.now()}_${Math.random()}`,
  name: name || file?.name || "image",
  size: size || (file ? `${Math.max(1, Math.round(file.size / 1024 / 1024))}MB` : ""),
  url: existingUrl || (file ? URL.createObjectURL(file) : ""),
  file,
  isExisting: Boolean(existingId),
});

const mapImageFromBackend = (img, fallbackPrefix = "image") => {
  if (!img) return null;

  const imageUrl = img.image || img.url || img.file || "";
  if (!imageUrl) return null;

  return createPreviewItem({
    existingId: img.id ?? null,
    existingUrl: imageUrl,
    name: img.name || `${fallbackPrefix}`,
    size: img.size || "",
  });
};

const ConfirmDeleteModal = ({ isOpen, onClose, onConfirm, brandName, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className="w-[400px] rounded-2xl bg-white shadow-2xl animate-scaleIn overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-center mb-4">
            <div className="h-14 w-14 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </div>
          
          <h3 className="text-xl font-semibold text-center text-gray-800 mb-2">
            Delete Brand
          </h3>
          
          <p className="text-center text-gray-600 mb-6">
            Are you sure you want to delete <span className="font-semibold text-gray-800">"{brandName}"</span>?<br />
            This action cannot be undone.
          </p>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 font-medium text-gray-700 transition-all hover:bg-gray-50 disabled:opacity-70"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 font-medium text-white transition-all hover:bg-red-700 hover:shadow-lg hover:shadow-red-600/25 disabled:opacity-70"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Deleting...
                </div>
              ) : (
                "Delete"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AdvancedSettingsModal({ onClose, serviceId, onSaved, role = "individual" }) {
  const isWorker = role === "worker";
  const [serviceImages, setServiceImages] = useState([]);
  const [brands, setBrands] = useState([]); 
  const [selectedBrand, setSelectedBrand] = useState(null); 
  const [brandTitle, setBrandTitle] = useState("");
  const [brandDescription, setBrandDescription] = useState("");
  const [brandImages, setBrandImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showBrandForm, setShowBrandForm] = useState(false); 
  const [isEditingBrand, setIsEditingBrand] = useState(false); 
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null); 

  const serviceInputRef = useRef(null);
  const brandInputRef = useRef(null);
  const mountedRef = useRef(false);
  const fetchAbortRef = useRef(null);

  const existingServiceImageIds = useMemo(
    () =>
      serviceImages
        .filter((item) => item.isExisting && typeof item.id === "number")
        .map((item) => item.id),
    [serviceImages],
  );

  const existingBrandImageIds = useMemo(
    () =>
      brandImages
        .filter((item) => item.isExisting && typeof item.id === "number")
        .map((item) => item.id),
    [brandImages],
  );

  const newServiceImageFiles = useMemo(
    () =>
      serviceImages
        .filter((item) => !item.isExisting && item.file)
        .map((item) => item.file),
    [serviceImages],
  );

  const newBrandImageFiles = useMemo(
    () =>
      brandImages
        .filter((item) => !item.isExisting && item.file)
        .map((item) => item.file),
    [brandImages],
  );

  const parseResponse = async (res) => {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  };

  const revokeNewPreview = (item) => {
    if (item && !item.isExisting && item.url) {
      URL.revokeObjectURL(item.url);
    }
  };

  const validateFiles = (files, maxAllowed, currentCount, label) => {
    const selected = Array.from(files || []);
    const validFiles = [];

    for (const file of selected) {
      const isValidType =
        file.type === "image/jpeg" ||
        file.type === "image/png" ||
        file.type === "image/jpg";

      if (!isValidType) {
        toast.error(`${label}: only .jpg and .png files are allowed.`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${label}: file size must be less than 5MB.`);
        continue;
      }

      validFiles.push(file);
    }

    const availableSlots = Math.max(0, maxAllowed - currentCount);

    if (validFiles.length > availableSlots) {
      toast.error(`${label}: you can upload maximum ${maxAllowed} images.`);
    }

    return validFiles.slice(0, availableSlots);
  };

  const handleFiles = (files, type) => {
    if (isWorker || loading) return;

    if (type === "service") {
      const prepared = validateFiles(
        files,
        MAX_SERVICE_IMAGES,
        serviceImages.length,
        "Service images",
      ).map((file) => createPreviewItem({ file }));

      if (prepared.length) {
        setServiceImages((prev) => [...prev, ...prepared]);
      }
      return;
    }

    const prepared = validateFiles(
      files,
      MAX_BRAND_IMAGES,
      brandImages.length,
      "Brand images",
    ).map((file) => createPreviewItem({ file }));

    if (prepared.length) {
      setBrandImages((prev) => [...prev, ...prepared]);
    }
  };

  const handleRemove = (type, index) => {
    if (isWorker || loading) return;

    if (type === "service") {
      setServiceImages((prev) => {
        const item = prev[index];
        revokeNewPreview(item);
        return prev.filter((_, i) => i !== index);
      });
      return;
    }

    setBrandImages((prev) => {
      const item = prev[index];
      revokeNewPreview(item);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    if (isWorker || loading) return;
    handleFiles(e.dataTransfer.files, type);
  };

  const handleBrowse = (type) => {
    if (isWorker || loading) return;
    if (type === "service") {
      serviceInputRef.current?.click();
    } else {
      brandInputRef.current?.click();
    }
  };

  const fetchAdvancedSettings = async () => {
    const token = getAccessToken();

    if (!token || !serviceId) {
      if (mountedRef.current) setInitialLoading(false);
      return;
    }

    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }

    const controller = new AbortController();
    fetchAbortRef.current = controller;

    try {
      const headers = buildHeaders(token);

      const res = await fetch(
        `${BASE_URL}/service/service-advanced-settings/${serviceId}/`,
        {
          method: "GET",
          headers,
          signal: controller.signal,
        },
      );

      if (!res.ok) {
        throw new Error("Failed to load service settings");
      }

      const data = await parseResponse(res);
      
      if (!mountedRef.current) return;

      if (Array.isArray(data?.service_images)) {
        const backendServiceImages = data.service_images
          .slice(0, MAX_SERVICE_IMAGES)
          .map((img) => mapImageFromBackend(img, "service-image"))
          .filter(Boolean);

        setServiceImages((prev) => {
          prev.forEach((item) => revokeNewPreview(item));
          return backendServiceImages;
        });
      }

      if (Array.isArray(data?.brands)) {
        setBrands(data.brands);
      }
      
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error("Error fetching advanced settings:", error);
      if (mountedRef.current) {
        toast.error(error.message || "Failed to load settings.");
      }
    } finally {
      if (mountedRef.current) {
        setInitialLoading(false);
      }
    }
  };

  const fetchBrandData = async (brandId) => {
    const token = getAccessToken();

    if (!token || !brandId) return;

    try {
      const headers = buildHeaders(token);

      const res = await fetch(
        `${BASE_URL}/service/service-brand-get/${brandId}/`,
        {
          method: "GET",
          headers,
        },
      );

      if (res.status === 404) {
        return null;
      }

      const data = await parseResponse(res);

      if (!res.ok) {
        throw new Error(formatBackendErrors(data));
      }

      return data;
    } catch (error) {
      console.error("Error fetching brand data:", error);
      toast.error(error.message || "Failed to load brand data.");
      return null;
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    fetchAdvancedSettings();

    return () => {
      mountedRef.current = false;
      if (fetchAbortRef.current) {
        fetchAbortRef.current.abort();
      }
    };
  }, [serviceId]);

  useEffect(() => {
    return () => {
      serviceImages.forEach((item) => revokeNewPreview(item));
      brandImages.forEach((item) => revokeNewPreview(item));
    };
  }, [serviceImages, brandImages]);

  const uploadServiceImages = async () => {
    if (isWorker) return null;
    
    const token = getAccessToken();

    if (!token) throw new Error("No access token found — please log in.");
    if (!serviceId) throw new Error("Service ID is required.");

    if (!newServiceImageFiles.length && existingServiceImageIds.length) {
      const formData = new FormData();
      formData.append("old_images", JSON.stringify(existingServiceImageIds));

      const res = await fetch(
        `${BASE_URL}/service/service-advanced-update-images/${serviceId}/`,
        {
          method: "PUT",
          headers: buildHeaders(token),
          body: formData,
        },
      );

      const data = await parseResponse(res);

      if (!res.ok) {
        throw new Error(formatBackendErrors(data));
      }

      return data;
    }

    if (!newServiceImageFiles.length) {
      return null;
    }

    if (!existingServiceImageIds.length) {
      const formData = new FormData();

      newServiceImageFiles.forEach((file) => {
        formData.append("images", file);
      });

      const res = await fetch(
        `${BASE_URL}/service/service-advanced-upload-images/${serviceId}/`,
        {
          method: "POST",
          headers: buildHeaders(token),
          body: formData,
        },
      );

      const data = await parseResponse(res);

      if (!res.ok) {
        throw new Error(formatBackendErrors(data));
      }

      return data;
    }

    const formData = new FormData();
    formData.append("old_images", JSON.stringify(existingServiceImageIds));

    newServiceImageFiles.forEach((file) => {
      formData.append("new_images", file);
    });

    const res = await fetch(
      `${BASE_URL}/service/service-advanced-update-images/${serviceId}/`,
      {
        method: "PUT",
        headers: buildHeaders(token),
        body: formData,
      },
    );

    const data = await parseResponse(res);

    if (!res.ok) {
      throw new Error(formatBackendErrors(data));
    }

    return data;
  };

  const saveBrand = async () => {
    if (isWorker) return null;
    
    const token = getAccessToken();

    if (!token) throw new Error("No access token found — please log in.");
    if (!serviceId) throw new Error("Service ID is required.");

    const hasBrandContent = brandTitle.trim() || brandDescription.trim() || brandImages.length > 0;

    if (!hasBrandContent) {
      return null;
    }

    if (!brandTitle.trim()) {
      throw new Error("Brand name is required.");
    }

    if (isEditingBrand && selectedBrand) {
      const formData = new FormData();
      formData.append("title", brandTitle.trim());
      formData.append("description", brandDescription.trim());
      formData.append("old_images", JSON.stringify(existingBrandImageIds));

      newBrandImageFiles.forEach((file) => {
        formData.append("new_images", file);
      });

      const res = await fetch(
        `${BASE_URL}/service/service-brand-update/${selectedBrand.id}/`,
        {
          method: "PUT",
          headers: buildHeaders(token),
          body: formData,
        },
      );

      const data = await parseResponse(res);

      if (!res.ok) {
        throw new Error(formatBackendErrors(data));
      }

      return data;
    } else {
      const formData = new FormData();
      formData.append("title", brandTitle.trim());
      formData.append("description", brandDescription.trim());

      newBrandImageFiles.forEach((file) => {
        formData.append("images", file);
      });

      const res = await fetch(`${BASE_URL}/service/service-brand-add/${serviceId}/`, {
        method: "POST",
        headers: buildHeaders(token),
        body: formData,
      });

      const data = await parseResponse(res);

      if (!res.ok) {
        throw new Error(formatBackendErrors(data));
      }

      return data;
    }
  };

  const deleteBrand = async (brandId) => {
    if (isWorker) return;
    
    const token = getAccessToken();

    if (!token) throw new Error("No access token found — please log in.");

    const res = await fetch(`${BASE_URL}/service/service-brand-delete/${brandId}/`, {
      method: "DELETE",
      headers: buildHeaders(token),
    });

    if (!res.ok) {
      const data = await parseResponse(res);
      throw new Error(formatBackendErrors(data));
    }

    return true;
  };

  const handleAddBrand = () => {
    if (isWorker) return;
    setSelectedBrand(null);
    setBrandTitle("");
    setBrandDescription("");
    setBrandImages([]);
    setIsEditingBrand(false);
    setShowBrandForm(true);
  };

  const handleEditBrand = async (brand) => {
    if (isWorker) return;
    setSelectedBrand(brand);
    setBrandTitle(brand.title || "");
    setBrandDescription(brand.description || "");
    
    const brandData = await fetchBrandData(brand.id);
    if (brandData && brandData.brand_images) {
      const backendBrandImages = brandData.brand_images
        .slice(0, MAX_BRAND_IMAGES)
        .map((img) => mapImageFromBackend(img, "brand-image"))
        .filter(Boolean);
      setBrandImages(backendBrandImages);
    } else {
      setBrandImages([]);
    }
    
    setIsEditingBrand(true);
    setShowBrandForm(true);
  };

  const handleDeleteBrandClick = (brand) => {
    if (isWorker) return;
    setBrandToDelete(brand);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (isWorker || !brandToDelete) return;
    
    try {
      setLoading(true);
      await deleteBrand(brandToDelete.id);
      toast.success(`Brand "${brandToDelete.title}" deleted successfully!`);
      await fetchAdvancedSettings();
      setShowDeleteConfirm(false);
      setBrandToDelete(null);
      
      if (showBrandForm && isEditingBrand && selectedBrand?.id === brandToDelete.id) {
        setShowBrandForm(false);
        setSelectedBrand(null);
        setBrandTitle("");
        setBrandDescription("");
        setBrandImages([]);
      }
    } catch (error) {
      toast.error(error.message || "Failed to delete brand.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setBrandToDelete(null);
  };

  const handleSaveBrand = async () => {
    if (isWorker) return;
    
    try {
      setLoading(true);
      await saveBrand();
      toast.success(isEditingBrand ? "Brand updated successfully!" : "Brand created successfully!");
      await fetchAdvancedSettings();
      setShowBrandForm(false);
      setSelectedBrand(null);
      setBrandTitle("");
      setBrandDescription("");
      setBrandImages([]);
    } catch (error) {
      toast.error(error.message || "Failed to save brand.");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (isWorker || loading) return;

    try {
      setLoading(true);
      await uploadServiceImages();
      toast.success("Advanced settings saved successfully!");
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to save advanced settings.");
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleCancelBrandForm = () => {
    setShowBrandForm(false);
    setSelectedBrand(null);
    setBrandTitle("");
    setBrandDescription("");
    setBrandImages([]);
  };

  // Для worker показываем только просмотр
  if (isWorker) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadeIn">
        <div className="relative flex max-h-[90vh] w-[680px] flex-col overflow-hidden rounded-2xl bg-gradient-to-br from-white to-gray-50 shadow-2xl animate-scaleIn">
          <div className="border-b border-gray-100 bg-white/50 backdrop-blur-sm px-6 py-5">
            <div className="flex items-center justify-between">
              <button
                onClick={onClose}
                className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-all hover:border-[#5563F6] hover:bg-[#F7F8FF] hover:text-[#5563F6]"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
              
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  Advanced Settings
                </h2>
              </div>
              
              <div className="w-24"></div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
            {initialLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#5563F6] border-t-transparent"></div>
                <p className="mt-4 text-gray-500">Loading your settings...</p>
              </div>
            ) : (
              <>
                {/* Service Images Section - только просмотр */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <FileImage className="h-5 w-5 text-[#5563F6]" />
                        Service Images
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Showcase your service with high-quality images
                      </p>
                    </div>
                    <div className="text-xs font-medium text-[#5563F6] bg-[#F7F8FF] px-3 py-1 rounded-full">
                      {serviceImages.length}/{MAX_SERVICE_IMAGES}
                    </div>
                  </div>

                  {serviceImages.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl">
                      No service images
                    </div>
                  ) : (
                    <div className="grid gap-3">
                      {serviceImages.map((file, index) => (
                        <div
                          key={file.id || index}
                          className="flex items-center justify-between rounded-xl border border-gray-100 bg-white p-3 shadow-sm"
                        >
                          <div className="flex items-center gap-4">
                            <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                              <img
                                src={file.url}
                                alt={file.name}
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800 line-clamp-1">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">{file.size}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Brands Section - только просмотр */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <Briefcase className="h-5 w-5 text-[#5563F6]" />
                        Brands
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Show your clients what brands you are using
                      </p>
                    </div>
                  </div>

                  {brands.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-xl">
                      No brands yet
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {brands.map((brand) => (
                        <div
                          key={brand.id}
                          className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
                        >
                          <div className="mb-3">
                            <h4 className="font-semibold text-gray-800">{brand.title}</h4>
                            <p className="text-sm text-gray-600 mt-1">{brand.description}</p>
                          </div>
                          
                          {brand.brand_images && brand.brand_images.length > 0 && (
                            <div className="flex gap-2 mt-3">
                              {brand.brand_images.slice(0, 3).map((img, idx) => (
                                <div key={idx} className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                                  <img
                                    src={img.image}
                                    alt={`${brand.title} ${idx + 1}`}
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer - только кнопка Back для worker */}
          <div className="border-t border-gray-100 bg-white/50 backdrop-blur-sm px-6 py-5">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="rounded-xl border border-gray-200 bg-white px-8 py-3 font-medium text-gray-700 transition-all hover:border-[#5563F6] hover:bg-[#F7F8FF] hover:text-[#5563F6]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadeIn">
        <div className="relative flex max-h-[90vh] w-[680px] flex-col overflow-hidden rounded-2xl bg-gradient-to-br from-white to-gray-50 shadow-2xl animate-scaleIn">
          {/* Header */}
          <div className="border-b border-gray-100 bg-white/50 backdrop-blur-sm px-6 py-5">
            <div className="flex items-center justify-between">
              <button
                onClick={showBrandForm ? handleCancelBrandForm : onClose}
                disabled={loading}
                className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-all hover:border-[#5563F6] hover:bg-[#F7F8FF] hover:text-[#5563F6] disabled:opacity-70"
              >
                <ArrowLeft className="h-4 w-4" />
                {showBrandForm ? "Back to Settings" : "Back"}
              </button>
              
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  {showBrandForm ? (isEditingBrand ? "Edit Brand" : "Add Brand") : "Advanced Settings"}
                </h2>
              </div>
              
              <div className="w-24"></div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8">
            {initialLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#5563F6] border-t-transparent"></div>
                <p className="mt-4 text-gray-500">Loading your settings...</p>
              </div>
            ) : showBrandForm ? (
              // Форма создания/редактирования бренда (только для не-worker)
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <ImageIcon className="h-5 w-5 text-[#5563F6]" />
                        Brand Images
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Upload up to {MAX_BRAND_IMAGES} images for your brand
                      </p>
                    </div>
                    <div className="text-xs font-medium text-[#5563F6] bg-[#F7F8FF] px-3 py-1 rounded-full">
                      {brandImages.length}/{MAX_BRAND_IMAGES}
                    </div>
                  </div>

                  <div
                    className="group relative overflow-hidden rounded-2xl border-2 border-dashed border-gray-200 bg-white/50 transition-all hover:border-[#5563F6] hover:bg-[#F7F8FF] cursor-pointer"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDrop(e, "brand")}
                    onClick={() => handleBrowse("brand")}
                  >
                    <div className="flex flex-col items-center justify-center p-8 text-center">
                      <div className="mb-4 rounded-full bg-gradient-to-r from-[#5563F6]/10 to-[#7C3AED]/10 p-4">
                        <Upload className="h-8 w-8 text-[#5563F6]" />
                      </div>
                      <p className="font-medium text-gray-700">
                        Drag & drop your images or{" "}
                        <span className="text-[#5563F6] hover:underline">browse</span>
                      </p>
                      <p className="mt-2 text-xs text-gray-500">
                        PNG, JPG up to 5MB (Max {MAX_BRAND_IMAGES} images)
                      </p>
                    </div>

                    <input
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png"
                      ref={brandInputRef}
                      className="hidden"
                      onChange={(e) => handleFiles(e.target.files, "brand")}
                    />
                  </div>

                  {brandImages.length > 0 && (
                    <div className="grid gap-3">
                      {brandImages.map((file, index) => (
                        <div
                          key={file.id || index}
                          className="group flex items-center justify-between rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition-all hover:shadow-md"
                        >
                          <div className="flex items-center gap-4">
                            <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                              <img
                                src={file.url}
                                alt={file.name}
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800 line-clamp-1">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">{file.size}</p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleRemove("brand", index)}
                            className="rounded-lg p-2 text-gray-400 transition-all hover:bg-red-50 hover:text-red-500"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Brand Name *
                  </label>
                  <input
                    type="text"
                    placeholder="Enter brand name"
                    value={brandTitle}
                    onChange={(e) => setBrandTitle(e.target.value)}
                    disabled={loading}
                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-800 placeholder-gray-400 transition-all focus:border-[#5563F6] focus:outline-none focus:ring-2 focus:ring-[#5563F6]/20 disabled:opacity-70"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Brand Description *
                  </label>
                  <textarea
                    placeholder="Describe the brand and its products..."
                    rows="4"
                    value={brandDescription}
                    onChange={(e) => setBrandDescription(e.target.value)}
                    disabled={loading}
                    className="w-full resize-none rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-800 placeholder-gray-400 transition-all focus:border-[#5563F6] focus:outline-none focus:ring-2 focus:ring-[#5563F6]/20 disabled:opacity-70"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  {isEditingBrand && (
                    <button
                      onClick={() => handleDeleteBrandClick(selectedBrand)}
                      disabled={loading}
                      className="flex-1 rounded-xl border border-red-200 bg-white px-4 py-3 font-medium text-red-600 transition-all hover:bg-red-50 hover:border-red-300 disabled:opacity-70"
                    >
                      <Trash2 className="h-4 w-4 inline mr-2" />
                      Delete Brand
                    </button>
                  )}
                  <button
                    onClick={handleSaveBrand}
                    disabled={loading || !brandTitle.trim() || !brandDescription.trim()}
                    className="flex-1 rounded-xl bg-gradient-to-r from-[#5563F6] to-[#7C3AED] px-4 py-3 font-medium text-white transition-all hover:shadow-lg hover:shadow-[#5563F6]/25 disabled:opacity-70"
                  >
                    {loading ? "Saving..." : (isEditingBrand ? "Update Brand" : "Create Brand")}
                  </button>
                </div>
              </div>
            ) : (
              // Основная форма Advanced Settings (только для не-worker)
              <>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <FileImage className="h-5 w-5 text-[#5563F6]" />
                        Service Images
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Showcase your service with high-quality images
                      </p>
                    </div>
                    <div className="text-xs font-medium text-[#5563F6] bg-[#F7F8FF] px-3 py-1 rounded-full">
                      {serviceImages.length}/{MAX_SERVICE_IMAGES}
                    </div>
                  </div>

                  <div
                    className="group relative overflow-hidden rounded-2xl border-2 border-dashed border-gray-200 bg-white/50 transition-all hover:border-[#5563F6] hover:bg-[#F7F8FF] cursor-pointer"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => handleDrop(e, "service")}
                    onClick={() => handleBrowse("service")}
                  >
                    <div className="flex flex-col items-center justify-center p-8 text-center">
                      <div className="mb-4 rounded-full bg-gradient-to-r from-[#5563F6]/10 to-[#7C3AED]/10 p-4">
                        <Upload className="h-8 w-8 text-[#5563F6]" />
                      </div>
                      <p className="font-medium text-gray-700">
                        Drag & drop your images or{" "}
                        <span className="text-[#5563F6] hover:underline">browse</span>
                      </p>
                      <p className="mt-2 text-xs text-gray-500">
                        PNG, JPG up to 5MB (Max {MAX_SERVICE_IMAGES} images)
                      </p>
                    </div>

                    <input
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png"
                      ref={serviceInputRef}
                      className="hidden"
                      onChange={(e) => handleFiles(e.target.files, "service")}
                    />
                  </div>

                  {serviceImages.length > 0 && (
                    <div className="grid gap-3">
                      {serviceImages.map((file, index) => (
                        <div
                          key={file.id || index}
                          className="group flex items-center justify-between rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition-all hover:shadow-md"
                        >
                          <div className="flex items-center gap-4">
                            <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                              <img
                                src={file.url}
                                alt={file.name}
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-800 line-clamp-1">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">{file.size}</p>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleRemove("service", index)}
                            className="rounded-lg p-2 text-gray-400 transition-all hover:bg-red-50 hover:text-red-500"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <Briefcase className="h-5 w-5 text-[#5563F6]" />
                        Brands
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Show your clients what brands you are using
                      </p>
                    </div>
                  </div>

                  {brands.length === 0 ? (
                    <div
                      className="group relative overflow-hidden rounded-2xl border-2 border-dashed border-gray-200 bg-white/50 transition-all hover:border-[#5563F6] hover:bg-[#F7F8FF] cursor-pointer"
                      onClick={handleAddBrand}
                    >
                      <div className="flex flex-col items-center justify-center p-8 text-center">
                        <div className="mb-4 rounded-full bg-gradient-to-r from-[#5563F6]/10 to-[#7C3AED]/10 p-4">
                          <Plus className="h-8 w-8 text-[#5563F6]" />
                        </div>
                        <p className="font-medium text-gray-700">
                          No brands yet.{" "}
                          <span className="text-[#5563F6] hover:underline">Add your first brand</span>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {brands.map((brand) => (
                        <div
                          key={brand.id}
                          className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-all hover:shadow-md"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-800">{brand.title}</h4>
                              <p className="text-sm text-gray-600 mt-1">{brand.description}</p>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditBrand(brand)}
                                className="rounded-lg px-3 py-1 text-sm font-medium text-[#5563F6] hover:bg-[#F7F8FF] transition-all"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteBrandClick(brand)}
                                className="rounded-lg px-3 py-1 text-sm font-medium text-red-600 hover:bg-red-50 transition-all"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          
                          {brand.brand_images && brand.brand_images.length > 0 && (
                            <div className="flex gap-2 mt-3">
                              {brand.brand_images.slice(0, 3).map((img, idx) => (
                                <div key={idx} className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                                  <img
                                    src={img.image}
                                    alt={`${brand.title} ${idx + 1}`}
                                    className="h-full w-full object-cover"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      
                      <button
                        onClick={handleAddBrand}
                        className="w-full rounded-xl border-2 border-dashed border-gray-200 bg-white/50 py-4 text-center text-gray-600 transition-all hover:border-[#5563F6] hover:bg-[#F7F8FF] hover:text-[#5563F6]"
                      >
                        <Plus className="h-5 w-5 inline mr-2" />
                        Add Another Brand
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer - только для основной формы и не-worker */}
          {!showBrandForm && !initialLoading && !isWorker && (
            <div className="border-t border-gray-100 bg-white/50 backdrop-blur-sm px-6 py-5">
              <div className="flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="group relative overflow-hidden rounded-xl bg-gradient-to-r from-[#5563F6] to-[#7C3AED] px-8 py-3 font-medium text-white transition-all hover:shadow-lg hover:shadow-[#5563F6]/25 disabled:opacity-70"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    {loading ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Save Changes
                      </>
                    )}
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        brandName={brandToDelete?.title || ""}
        loading={loading}
      />
    </>
  );
}